import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import SupportForm from './SupportForm';
import { useAuth } from '../../hooks/useAuth';

const planLabel = (plan: string) => {
  const labels: Record<string, string> = {
    free: 'Free',
    plus: 'Plus',
    pro: 'Pro',
    freemium_trial: 'Trial (7 days)',
    pay_per_scan: 'Pay-per-scan',
  };
  return labels[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
};

interface SettingsContentProps {
  category: string;
}

const SettingsContent: React.FC<SettingsContentProps> = ({ category }) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [supportModalVisible, setSupportModalVisible] = useState(false);

  const renderAccountContent = () => (
    <View style={styles.container}>
      <Text style={[styles.title, { color: colors.text }]}>
        Account Settings
      </Text>

      {/* Email Section */}
      <View style={[styles.section, { borderBottomColor: colors.border }]}>
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Email
        </Text>
        <Text style={[styles.value, { color: colors.text }]}>
          user@example.com
        </Text>
      </View>

      {/* Change Password Section */}
      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() =>
          navigation.navigate('ChangePassword', { mode: 'change' })
        }
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Change Password
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Update your password
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      {/* Profile Information Section */}
      <View style={[styles.section, { borderBottomColor: colors.border }]}>
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Profile Information
        </Text>
        <Text style={[styles.value, { color: colors.text }]}>
          Manage your profile details
        </Text>
      </View>

      {/* Account Security Section */}
      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() =>
          Alert.alert(
            'Coming Soon',
            'Account security features will be available soon.'
          )
        }
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Account Security
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Two-factor authentication, login history
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      {/* Data & Privacy Section */}
      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() =>
          Alert.alert(
            'Coming Soon',
            'Data & privacy features will be available soon.'
          )
        }
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Data & Privacy
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Download your data, privacy settings
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      {/* Delete Account Section */}
      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => {
          Alert.alert(
            'Delete Account',
            'Are you sure you want to permanently delete your account? This action cannot be undone.',
            [
              { text: 'Cancel', style: 'cancel' },
              {
                text: 'Delete',
                style: 'destructive',
                onPress: () =>
                  Alert.alert(
                    'Coming Soon',
                    'Account deletion will be available soon.'
                  ),
              },
            ]
          );
        }}
      >
        <Text style={[styles.subtitle, { color: '#EF4444' }]}>
          Delete Account
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: '#EF4444' }]}>
            Permanently delete your account
          </Text>
          <Ionicons name="chevron-forward" size={20} color="#EF4444" />
        </View>
      </TouchableOpacity>
    </View>
  );

  const renderPreferencesContent = () => (
    <View style={styles.container}>
      <Text style={[styles.title, { color: colors.text }]}>Preferences</Text>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('ThemeSelection')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Theme
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.text }]}>Light</Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('LanguageSelection')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Language
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.text }]}>English</Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('AccessibilitySettings')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Accessibility
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Configure accessibility options
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>
    </View>
  );

  const renderBillingContent = () => {
    const currentPlan = user?.subscription_plan || 'free';

    return (
      <View style={styles.container}>
        <Text style={[styles.title, { color: colors.text }]}>
          Billing & Subscription
        </Text>

        <View style={[styles.section, { borderBottomColor: colors.border }]}>
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Current Plan
          </Text>
          <Text style={[styles.value, { color: colors.text }]}>
            {planLabel(currentPlan)}
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.section, { borderBottomColor: colors.border }]}
          onPress={() => navigation.navigate('Pricing')}
        >
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Upgrade Plan
          </Text>
          <View style={styles.sectionContent}>
            <Text style={[styles.value, { color: colors.primary }]}>
              {currentPlan === 'pro'
                ? 'Manage subscription'
                : 'Upgrade to Plus or Pro'}
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.section, { borderBottomColor: colors.border }]}
          onPress={() => navigation.navigate('Billing')}
        >
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Payment Methods
          </Text>
          <View style={styles.sectionContent}>
            <Text style={[styles.value, { color: colors.primary }]}>
              Manage payment methods
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.section, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Billing history will be available soon.'
            )
          }
        >
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Billing History
          </Text>
          <View style={styles.sectionContent}>
            <Text style={[styles.value, { color: colors.primary }]}>
              View past invoices
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.section, { borderBottomColor: colors.border }]}
          onPress={() => {
            Alert.alert(
              'Cancel Subscription',
              'Are you sure you want to cancel your subscription?',
              [
                { text: 'Keep Subscription', style: 'cancel' },
                {
                  text: 'Cancel Subscription',
                  style: 'destructive',
                  onPress: () =>
                    Alert.alert(
                      'Coming Soon',
                      'Subscription cancellation will be available soon.'
                    ),
                },
              ]
            );
          }}
        >
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Cancel Subscription
          </Text>
          <View style={styles.sectionContent}>
            <Text style={[styles.value, { color: '#EF4444' }]}>
              Cancel your subscription
            </Text>
            <Ionicons name="chevron-forward" size={20} color="#EF4444" />
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  const renderAppContent = () => (
    <View style={styles.container}>
      <Text style={[styles.title, { color: colors.text }]}>App Settings</Text>

      <View style={[styles.section, { borderBottomColor: colors.border }]}>
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Push Notifications
        </Text>
        <Text style={[styles.value, { color: colors.text }]}>Enabled</Text>
      </View>

      <View style={[styles.section, { borderBottomColor: colors.border }]}>
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Email Notifications
        </Text>
        <Text style={[styles.value, { color: colors.text }]}>Disabled</Text>
      </View>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() =>
          Alert.alert(
            'About ColourSense',
            'Version 1.0.0\n\nA smart color analysis app that helps you discover your perfect colors with AI-powered analysis, virtual draping, and colour generation.\n\nFeatures:\n• AI-powered color analysis\n• Virtual draping\n• Colour Generation'
          )
        }
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          About ColourSense
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Version 1.0.0
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('PrivacyPolicy')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Privacy Policy
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            View privacy policy
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('TermsOfService')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Terms of Service
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            View terms of service
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => navigation.navigate('HelpCenter')}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Help & Support
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Get help and support
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.section, { borderBottomColor: colors.border }]}
        onPress={() => setSupportModalVisible(true)}
      >
        <Text style={[styles.subtitle, { color: colors.secondary }]}>
          Contact Support
        </Text>
        <View style={styles.sectionContent}>
          <Text style={[styles.value, { color: colors.primary }]}>
            Get help from our support team
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        </View>
      </TouchableOpacity>

      <SupportForm
        visible={supportModalVisible}
        onClose={() => setSupportModalVisible(false)}
        onSuccess={() => {
          Alert.alert(
            'Success',
            'Your support request has been submitted. We will get back to you within 24 hours.'
          );
        }}
      />
    </View>
  );

  switch (category) {
    case 'Account':
      return renderAccountContent();
    case 'Billing':
      return renderBillingContent();
    case 'Preferences':
      return renderPreferencesContent();
    case 'App':
      return renderAppContent();
    default:
      return (
        <View style={styles.container}>
          <Text style={[styles.title, { color: colors.text }]}>
            Select a settings category from the sidebar
          </Text>
        </View>
      );
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 24,
  },
  section: {
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  value: {
    fontSize: 12,
    fontWeight: '400',
  },
  sectionContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default SettingsContent;
